import { CellContext, ColumnDef } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { FlexBox } from 'components/Box'
import { ProductsHeaderMenu } from 'components/Table'
import { TooltipProps } from 'components/Tooltip'
import { Alert } from 'features/alerts/types/alert'
import { AdUnitAlerts } from 'features/inventory/ad-unit/components/AdUnitAlerts'
import { usePriceGeniusAlwaysOn } from 'features/inventory/ad-unit/hooks/usePriceGeniusAlwaysOn'
import { AdUnitAlert } from 'features/inventory/ad-unit/types/adUnitAlert'
import { Product } from 'features/product'
import { useAbility } from 'providers/casl'
import { ProductSwitchCell } from '../../components/ProductSwitchCell'
import { AdUnitForTable } from '../../types'
import { useSwitchAdUnitsProduct } from '../useSwitchAdUnitsProduct'
import { UseProductSwitchColumns } from './types'

export const useProductSwitchColumns = ({
  tableFilters,
  tableItemsCount,
}: UseProductSwitchColumns): ColumnDef<AdUnitForTable>[] => {
  const ability = useAbility()
  const { t: tAdUnit } = useTranslation('features/adUnit')
  const { t: tProduct } = useTranslation('features/product')

  const { overwritePriceGeniusState } = usePriceGeniusAlwaysOn()
  const { update } = useSwitchAdUnitsProduct()

  const productsSwitches: Product[] = [
    'prebidStack',
    'refresher',
    'viewabilityTools',
    'priceGenius',
    'unfilledRecovery',
  ]

  const column = (product: Product): ColumnDef<AdUnitForTable> => {
    const noProductAccess: boolean = ability.cannot('access', `Product-${product}`)

    const isNotAuthorized: boolean =
      noProductAccess || ability.cannot('update', `Product-${product}`)

    const handleToggleProductState = (enabled: boolean): void =>
      void update({ enabledValue: enabled, productType: product, terms: tableFilters })

    const composeTooltip = (): TooltipProps | undefined => {
      if (noProductAccess) {
        return {
          content: tProduct('featureUnavailable'),
        }
      } else return undefined
    }

    const Cell = (props: CellContext<AdUnitForTable, unknown>): JSX.Element => {
      const { alerts } = props.row.original

      const isDisabledBasedOnOutOfPage: boolean =
        product === 'viewabilityTools' && props.row.original.outOfPage

      return (
        <FlexBox axis='x'>
          <ProductSwitchCell
            {...props}
            disabled={
              isNotAuthorized || overwritePriceGeniusState(product) || isDisabledBasedOnOutOfPage
            }
            product={product}
          />

          {alerts.length > 0 && (
            <AdUnitAlerts
              alerts={alerts.filter((alert: Alert<AdUnitAlert>): boolean =>
                alert.name.startsWith(product),
              )}
            />
          )}
        </FlexBox>
      )
    }

    return {
      accessorKey: `${product}Enabled`,
      cell: props => <Cell {...props} />,
      header: tProduct(`product.${product}`),
      meta: {
        disabledHeader: isNotAuthorized,
        headerMenu: (
          <ProductsHeaderMenu
            i18nResource={tAdUnit}
            productState={{
              disabledToggle: isNotAuthorized || tableItemsCount === 0,
              onToggle: handleToggleProductState,
            }}
          />
        ),
        nonClickable: true,
        tooltip: composeTooltip(),
      },
    }
  }

  return productsSwitches.map((product: Product): ColumnDef<AdUnitForTable> => column(product))
}
